import { GameState, Player, TrainingFocus } from '../../../domain/entities';
import { addNewsItem } from '../../GameService';

/**
 * TrainingProcessor - Handles player training and development
 *
 * Responsibilities:
 * - Process rest training (double fitness recovery)
 * - Process skill development
 * - Process attribute development based on focus
 * - Process fitness training (fitnessMax/fitnessRecovery improvement)
 * - Track development history
 * - Generate development news
 */
export class TrainingProcessor {
    /**
     * Process training and development for all eligible players
     */
    static processTrainingAndDevelopment(
        gameState: GameState,
        playersWhoPlayed: Set<number>
    ): void {
        Object.values(gameState.leagues).forEach(league => {
            league.teams.forEach(team => {
                const isPlayerTeam = team.name === gameState.playerTeamName;
                const facilityLevel = team.trainingFacilities.level;
                const facilityModifier = 1 + (facilityLevel - 1) * 0.15; // 15% boost per level, up to 1.75x at level 6

                team.squad.forEach(player => {
                    // Determine effective training focus (player > positional > team)
                    const effectiveFocus = player.trainingFocus ||
                                         team.positionalTrainingFocus[player.position] ||
                                         team.trainingFocus;

                    // Handle Rest training
                    if (effectiveFocus === 'Rest') {
                        this.processRestTraining(player, playersWhoPlayed);
                        return; // No skill/attribute gain when resting
                    }

                    // Skip development if maxed out, old, or injured
                    if (player.skill >= player.potential || player.age > 29 || player.injury) {
                        return;
                    }

                    // Process skill development
                    this.processSkillDevelopment(
                        player,
                        playersWhoPlayed,
                        facilityModifier,
                        gameState,
                        isPlayerTeam
                    );

                    // Process attribute/fitness development based on focus
                    this.processFocusedTraining(
                        player,
                        effectiveFocus,
                        facilityModifier,
                        gameState,
                        isPlayerTeam
                    );
                });
            });
        });
    }

    /**
     * Handle rest training - double recovery for players who didn't play
     */
    private static processRestTraining(
        player: Player,
        playersWhoPlayed: Set<number>
    ): void {
        if (!playersWhoPlayed.has(player.id)) {
            player.fitness = Math.min(100, player.fitness + player.fitnessRecovery * 2.0);
        }
    }

    /**
     * Process general skill development
     */
    private static processSkillDevelopment(
        player: Player,
        playersWhoPlayed: Set<number>,
        facilityModifier: number,
        gameState: GameState,
        isPlayerTeam: boolean
    ): void {
        let skillGainChance = 0.05; // Base 5%

        // Age-based bonuses
        if (player.age < 21) skillGainChance += 0.1;      // +10% for teenagers
        else if (player.age < 24) skillGainChance += 0.05; // +5% for young players

        // Match experience bonus
        if (playersWhoPlayed.has(player.id)) {
            skillGainChance += 0.02; // +2% for playing
        }

        // High potential bonus
        if (player.potential - player.skill > 10) {
            skillGainChance += 0.05; // +5% when far from potential
        }

        // Difficulty penalty for players over 100 skill
        // The higher the skill above 100, the harder it becomes to progress
        if (player.skill > 100) {
            const skillsAbove100 = player.skill - 100;
            const difficultyPenalty = Math.pow(0.5, skillsAbove100 / 5); // Halves every 5 skill points above 100
            skillGainChance *= difficultyPenalty;
        }

        // Apply facility modifier
        skillGainChance *= facilityModifier;

        if (Math.random() < skillGainChance) {
            // Cap skill at 120 (absolute maximum), not just potential
            player.skill = Math.min(120, Math.min(player.potential, player.skill + 1));

            if (!player.developmentHistory) player.developmentHistory = [];
            player.developmentHistory.push({
                season: gameState.season,
                week: Math.floor(gameState.currentWeek),
                type: 'skill',
                description: `Overall Skill`,
                change: 1,
            });

            if (isPlayerTeam) {
                addNewsItem(
                    gameState,
                    'Player Development',
                    `${player.name} has improved their overall skill to ${player.skill} through training.`,
                    'general'
                );
            }
        }
    }

    /**
     * Process focused training (attributes or fitness)
     */
    private static processFocusedTraining(
        player: Player,
        focus: TrainingFocus,
        facilityModifier: number,
        gameState: GameState,
        isPlayerTeam: boolean
    ): void {
        if (focus === 'Fitness') {
            this.processFitnessTraining(player, facilityModifier, gameState);
        } else {
            this.processAttributeTraining(player, focus, facilityModifier, gameState, isPlayerTeam);
        }
    }

    /**
     * Process fitness training (improve fitnessMax or fitnessRecovery)
     */
    private static processFitnessTraining(
        player: Player,
        facilityModifier: number,
        gameState: GameState
    ): void {
        let fitnessGainChance = 0.05 * facilityModifier;

        // Diminishing returns for elite stamina (95+)
        if (player.fitnessMax >= 95 || player.fitnessRecovery >= 15) {
            fitnessGainChance *= 0.2; // 80% harder to improve elite fitness
        }

        if (Math.random() < fitnessGainChance) {
            if (!player.developmentHistory) player.developmentHistory = [];

            // 50/50 chance between fitnessMax and fitnessRecovery
            if (Math.random() < 0.5 && player.fitnessMax < 100) {
                player.fitnessMax++;
                player.developmentHistory.push({
                    season: gameState.season,
                    week: Math.floor(gameState.currentWeek),
                    type: 'physical',
                    description: `Max Fitness`,
                    change: 1
                });
            } else if (player.fitnessRecovery < 19) {
                player.fitnessRecovery++;
                player.developmentHistory.push({
                    season: gameState.season,
                    week: Math.floor(gameState.currentWeek),
                    type: 'physical',
                    description: `Fitness Recovery`,
                    change: 1
                });
            }
        }
    }

    /**
     * Process attribute training (position-specific or general)
     */
    private static processAttributeTraining(
        player: Player,
        focus: TrainingFocus,
        facilityModifier: number,
        gameState: GameState,
        isPlayerTeam: boolean
    ): void {
        const attrGainChance = 0.1 * facilityModifier;
        const attributesToTrain = this.getAttributesForFocus(focus, player);

        if (attributesToTrain.length > 0 && Math.random() < attrGainChance) {
            const attrToImprove = attributesToTrain[Math.floor(Math.random() * attributesToTrain.length)];

            if (player.attributes[attrToImprove] < 99) {
                player.attributes[attrToImprove]++;

                if (!player.developmentHistory) player.developmentHistory = [];
                player.developmentHistory.push({
                    season: gameState.season,
                    week: Math.floor(gameState.currentWeek),
                    type: 'attribute',
                    description: `${attrToImprove.charAt(0).toUpperCase() + attrToImprove.slice(1)}`,
                    change: 1,
                });

                if (isPlayerTeam) {
                    addNewsItem(
                        gameState,
                        'Attribute Improvement',
                        `${player.name} has improved their ${attrToImprove} attribute thanks to focused training.`,
                        'general',
                        undefined,
                        { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id }
                    );
                }
            }
        }
    }

    /**
     * Get list of attributes to train based on focus
     */
    private static getAttributesForFocus(focus: TrainingFocus, player: Player): string[] {
        switch (focus) {
            case 'Goalkeeping':
                return ['handling', 'reflexes', 'positioning'];
            case 'Defending':
                return ['tackling', 'marking', 'heading', 'positioning'];
            case 'Midfield':
                return ['passing', 'dribbling', 'creativity', 'tackling'];
            case 'Attacking':
                return ['finishing', 'dribbling', 'pace', 'creativity'];
            case 'General':
                return Object.keys(player.attributes);
            default:
                return [];
        }
    }
}
